import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Check } from 'lucide-react';
import AuthShell from '../../components/auth/AuthShell';
import { AuthHeading, AuthField, PasswordInput, PasswordStrength, ErrorBanner, PrimaryButton } from '../../components/auth/AuthPrimitives';
import api from '../../utils/axios';

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const { uidb64, token } = useParams();
  const [pw, setPw] = useState('');
  const [cpw, setCpw] = useState('');
  const [errors, setErrors] = useState({});
  const [bannerError, setBannerError] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  function validate() {
    const errs = {};
    if (!pw) errs.pw = 'Create a password';
    else if (pw.length < 8) errs.pw = 'At least 8 characters';
    if (!cpw) errs.cpw = 'Confirm your password';
    else if (cpw !== pw) errs.cpw = "Passwords don't match";
    return errs;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setLoading(true);
    setBannerError('');
    try {
      await api.post('/accounts/auth/password/reset/confirm/', { uidb64, token, new_password: pw, new_password2: cpw });
      setDone(true);
    } catch (err) {
      setBannerError(err.response?.data?.error || err.response?.data?.detail || 'This reset link is invalid or has expired. Request a new one.');
    } finally {
      setLoading(false);
    }
  }

  if (done) {
    return (
      <AuthShell onLogoClick={() => navigate('/login')}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ width: 56, height: 56, background: '#000', color: '#fff', borderRadius: 999, margin: '0 auto 24px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
            <Check size={28} />
          </div>
          <AuthHeading align="center" title="Password updated" subtitle="You can now sign in with your new password." />
          <div style={{ marginTop: 32 }}>
            <PrimaryButton onClick={() => navigate('/login')}>Continue to Sign In</PrimaryButton>
          </div>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell onLogoClick={() => navigate('/login')}>
      <form onSubmit={handleSubmit} noValidate>
        <AuthHeading title="Set a new password" subtitle="Choose a strong password you don't use anywhere else. We recommend a passphrase of 12+ characters." />
        <ErrorBanner>{bannerError}</ErrorBanner>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <AuthField label="New password" htmlFor="rp-pw" error={errors.pw} hint={!errors.pw ? 'At least 8 characters.' : null}>
            <PasswordInput id="rp-pw" value={pw} onChange={e => setPw(e.target.value)} error={errors.pw} autoComplete="new-password" />
            <PasswordStrength value={pw} />
          </AuthField>
          <AuthField label="Confirm new password" htmlFor="rp-cpw" error={errors.cpw}>
            <PasswordInput id="rp-cpw" value={cpw} onChange={e => setCpw(e.target.value)} error={errors.cpw} autoComplete="new-password" />
          </AuthField>
        </div>
        <div style={{ marginTop: 20 }}>
          <PrimaryButton type="submit" loading={loading}>Update Password</PrimaryButton>
        </div>
      </form>
    </AuthShell>
  );
}
